import { useMemo, useRef, useState } from "react"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useSave } from "lib/@hooks/use-save"
import { Bound } from "lib/@components/binding/Bound"
import { Alert } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

/**
 * Edits a record with the given parameters. Optionally allows cloning of the initial value
 * and debouncing of the save action.
 *
 * @param {Object} options - The options for editing the record.
 * @param {Object} options.target - The record to be edited.
 * @param {string} options.id - The optional unique identifier for the record.
 * @param {boolean} options.clone - Indicates whether the initial value of target should be cloned.
 * @param {Function} options.store - The function to store the record.
 * @param {number} options.debounceTime=1000 - The debounce time for the save action.
 * @param {React.ReactNode} options.children - The children elements to be rendered.
 *
 * @return {React.ReactElement} - The component rendering the editable record.
 */
export function EditRecord({
    target: initialValue,
    field,
    initialise,
    id,
    clone,
    store = async () => Promise.resolve(true),
    debounceTime,
    children,
}) {
    const context = useBoundContext()
    const [initialisedRecord] = useState({})
    const _id = useRef()
    let fieldValue
    if (field) {
        fieldValue = Object.get(context.target, field, true)
    }
    const finalValue = initialValue ?? fieldValue ?? (initialise ? initialisedRecord : undefined)
    const target = useMemo(() => (clone ? structuredClone(finalValue) : finalValue), [finalValue, clone])

    _id.current = initialValue?._id ?? initialValue?.id ?? context.target?._id ?? context.target?.id

    const refresh = useRefresh()
    const onChange = useSave({ save, target, wait: debounceTime, id })
    if (!!field && !!initialValue) {
        return <Alert severity="error">You cannot specify both field and target!</Alert>
    }
    return (
        <Bound target={target} refresh={refresh} onChange={onChange}>
            {children}
        </Bound>
    )

    function save(target) {
        if (field) {
            Object.set(context.target, field, target)
            context.refresh?.()
        }
        if (_id?.current) {
            store(target, _id.current)
        }
    }
}
